import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import type Carousel from 'react-multi-carousel';

// react-multi-carousel recalculates item widths on window resize only, so a container that
// changes width on its own — the collapsing sidebar, a widget in a half-width row — leaves
// items sized for the old width. Returns the observed width so callers can lay out from the
// container instead of the viewport; null until the first measurement.
export const useCarouselContainerResize = (
  containerRef: RefObject<HTMLElement | null>,
  carouselRef: RefObject<InstanceType<typeof Carousel> | null>
): number | null => {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // ResizeObserver also fires on height changes; re-measuring the carousel then would
    // write layout from inside the observer callback for nothing.
    let lastWidth = -1;
    const measure = () => {
      const next = container.clientWidth;
      if (next === lastWidth) return;
      lastWidth = next;
      setWidth(next);
      // onResize is the public entry point but is absent from the package's types entry
      // (`types` points at lib/types.d.ts, not lib/Carousel.d.ts). It delegates here, and
      // would pass false under infinite={false}, skipping the position correction we need.
      carouselRef.current?.setItemsToShow(true);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, carouselRef]);

  return width;
};
